import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth.tsx';
import { NavigationRoutes } from './routes.ts';
import { useGlobalStore } from '../store/store.tsx';
import { UserRoleType } from '../types/userRoleType.ts';
import { ProfileService } from '../../features/profile/Services/ProfileService.ts';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState } from 'react';

export const ProtectRoute = ({ children }) => {
    const { tokens } = useAuth();
    const { profile, setProfile } = useGlobalStore();
    const location = useLocation();
    const profileService = new ProfileService();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!profile) {
                setIsLoading(true);
                try {
                    const fetchedProfile = await profileService.getProfile();
                    setProfile(fetchedProfile);
                } catch (error) {
                    console.error('Kunde inte hämta profilen', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, [profile, setProfile, profileService]);

    if (!tokens) {
        // user is not authenticated
        return <Navigate to={NavigationRoutes.login} />;
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <CircularProgress />
            </div>
        );
    }

    const regexAdmin = /^\/admin(\/|$)/;
    const regexSuperAdmin = /^\/admin\/super(\/|$)/;
    const regexUserManagement = /^\/admin\/user-management(\/|$)/;
    const regexLabelPrinting = /^\/admin\/label-printing(\/|$)/;

    if (
        regexUserManagement.test(location?.pathname) ||
        regexLabelPrinting.test(location?.pathname)
    ) {
        if (
            profile?.userRoles.some(
                (role) =>
                    role.roleName === UserRoleType.LocalAdmin ||
                    role.roleName === UserRoleType.OperationalManager ||
                    role.roleName === UserRoleType.Admin ||
                    role.roleName === UserRoleType.Root
            )
        ) {
            return children;
        } else {
            return <Navigate to={NavigationRoutes.admin} />;
        }
    }

    if (regexAdmin.test(location?.pathname)) {
        if (
            profile?.userRoles.some(
                (role) =>
                    role.roleName === UserRoleType.LocalAdmin ||
                    role.roleName === UserRoleType.MedicationsSafetyOfficer ||
                    role.roleName ===
                        UserRoleType.ControlledSubstancesOfficer ||
                    role.roleName === UserRoleType.OperationalManager ||
                    role.roleName === UserRoleType.Admin ||
                    role.roleName === UserRoleType.Root
            )
        ) {
            // user is authenticated and has admin roles
            return children;
        } else {
            // user is authenticated but does not have admin roles
            return <Navigate to={NavigationRoutes.login} />;
        }
    } else if (regexSuperAdmin.test(location?.pathname)) {
        if (
            profile?.userRoles.some(
                (role) =>
                    role.roleName === UserRoleType.Admin ||
                    role.roleName === UserRoleType.Root
            )
        ) {
            // user is authenticated and has super admin roles
            return children;
        } else {
            // user is authenticated but does not have super admin roles
            return <Navigate to={NavigationRoutes.login} />;
        }
    } else if (
        profile?.userRoles.some(
            (role) =>
                role.roleName.toLowerCase().trim() ===
                UserRoleType.Basic.toLowerCase().trim()
        )
    ) {
        // user is authenticated and is a basic user
        return children;
    }

    if (!profile) {
        return <Navigate to={NavigationRoutes.login} />;
    }

    return children;
};
